import {
  FURNACE_AFUE_GAS,
  FURNACE_AFUE_OIL,
  GAS_USD_PER_THERM,
  HEAT_PUMP_25C_CREDIT_USD,
  HEAT_PUMP_COOLING_EFFICIENCY_GAIN,
  HEAT_PUMP_COP,
  HEAT_PUMP_GROSS_USD,
  KWH_PER_THERM,
  OIL_USD_PER_THERM,
} from './assumptions';
import { EnergyLoads, MeasureApplication, SimulationContext } from './types';

const FREE_NO_OP = { grossUsd: 0, incentiveUsd: 0, measure: 'HEAT_PUMP', netUsd: 0 } as const;

// Convert each month's remaining fossil heating spend into heat pump kWh:
// dollars → therms → delivered heat (AFUE) → electricity at COP.
const fossilMonthToHeatPumpKwh = (fossilUsd: number, pricePerTherm: number, afue: number): number =>
  ((fossilUsd / pricePerTherm) * KWH_PER_THERM * afue) / HEAT_PUMP_COP;

export const applyHeatPump = (loads: EnergyLoads, ctx: SimulationContext): MeasureApplication => {
  if (ctx.heatingFuel === 'HEAT_PUMP' || ctx.heatingFuel === 'NONE') {
    return { loads, measureCost: FREE_NO_OP };
  }

  const keepCooling = 1 - HEAT_PUMP_COOLING_EFFICIENCY_GAIN;
  const coolingKwh = loads.coolingKwh.map((kwh) => kwh * keepCooling);

  const isFossil = ctx.heatingFuel === 'GAS' || ctx.heatingFuel === 'OIL';
  const pricePerTherm = ctx.heatingFuel === 'OIL' ? OIL_USD_PER_THERM : GAS_USD_PER_THERM;
  const afue = ctx.heatingFuel === 'OIL' ? FURNACE_AFUE_OIL : FURNACE_AFUE_GAS;

  const heatingKwh = isFossil
    ? loads.heatingKwh.map(
        (kwh, month) => kwh + fossilMonthToHeatPumpKwh(loads.fossilHeatUsd[month], pricePerTherm, afue)
      )
    : loads.heatingKwh.map((kwh) => kwh / HEAT_PUMP_COP);

  return {
    loads: {
      ...loads,
      coolingKwh,
      fossilHeatUsd: isFossil ? loads.fossilHeatUsd.map(() => 0) : loads.fossilHeatUsd,
      heatingKwh,
    },
    measureCost: {
      grossUsd: HEAT_PUMP_GROSS_USD,
      incentiveUsd: HEAT_PUMP_25C_CREDIT_USD,
      measure: 'HEAT_PUMP',
      netUsd: HEAT_PUMP_GROSS_USD - HEAT_PUMP_25C_CREDIT_USD,
    },
  };
};
